import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { clsx } from 'clsx';
import { useEffect, useState, type ReactNode, type RefObject } from 'react';

interface TooltipProps {
    title: ReactNode;
    children: ReactNode;
    className?: string;
    labelClassName?: string;
    triggerRef?: RefObject<HTMLElement | HTMLButtonElement | null>;
    show?: boolean;
    onOpenChange?: (open: boolean) => void;
}

// Unified tooltip chrome — teal-tinted in light, elevated card in dark (per Figma).
const tooltipChrome =
    'w-max max-w-80 rounded-md bg-teal-50 p-5 text-base font-medium text-muted-foreground shadow-lg dark:bg-neutral-0';

const Tooltip = ({
    className,
    title,
    children,
    labelClassName,
    triggerRef,
    show = false,
    onOpenChange,
}: TooltipProps) => {
    const [position, setPosition] = useState<{ top: string; left: string } | null>(null);

    // Update tooltip position for fixed positioning
    useEffect(() => {
        const updatePosition = () => {
            if (triggerRef?.current && show) {
                const rect = triggerRef.current.getBoundingClientRect();
                setPosition({
                    top: `${rect.top + window.scrollY - 12}px`,
                    left: `${rect.left + window.scrollX + rect.width / 2}px`,
                });
            }
        };

        updatePosition();
        if (show) {
            window.addEventListener('scroll', updatePosition);
            window.addEventListener('resize', updatePosition);
            return () => {
                window.removeEventListener('scroll', updatePosition);
                window.removeEventListener('resize', updatePosition);
            };
        }
    }, [show, triggerRef]);

    // If using fixed positioning with triggerRef
    if (triggerRef && onOpenChange) {
        return (
            <>
                <div
                    className={labelClassName}
                    onMouseEnter={() => onOpenChange(true)}
                    onMouseLeave={() => onOpenChange(false)}
                >
                    {children}
                </div>

                {/* Fixed positioning tooltip */}
                {show && position && (
                    <div
                        className={clsx('fixed z-50', tooltipChrome)}
                        style={{
                            top: position.top,
                            left: position.left,
                            transform: 'translate(-70%, -100%)',
                        }}
                    >
                        {title}
                    </div>
                )}
            </>
        );
    }

    // Default popover-based behavior
    return (
        <Popover className={clsx('relative', className)}>
            <PopoverButton as="div" className={clsx('cursor-pointer', labelClassName)}>
                {children}
            </PopoverButton>
            <PopoverPanel
                anchor={{ to: 'top', gap: '8px', padding: '16px' }}
                transition
                className={clsx(
                    'z-50',
                    tooltipChrome,
                    'transition duration-200 ease-out data-[closed]:translate-y-1 data-[closed]:opacity-0'
                )}
            >
                {title}
            </PopoverPanel>
        </Popover>
    );
};

export default Tooltip;
